import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Mail, Phone, Building } from 'lucide-react';
import './LegalPages.css';

const Imprint = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <div className="legal-header-content">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={20} />
            <span>{t('imprint.backButton')}</span>
          </button>
          <div className="legal-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/assets/logo/alpenscreen-horizontal-transparent.svg" alt="AlpenScreen" className="logo-img" />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="legal-container">
        <div className="legal-content">
          <div className="legal-header-section">
            <Building size={48} color="#2B7FD7" />
            <h1 className="legal-title">{t('imprint.title')}</h1>
            <p className="legal-subtitle">{t('imprint.subtitle')}</p>
          </div>

          {/* Provider Section */}
          <section className="legal-section">
            <h2>{t('imprint.provider.title')}</h2>
            <div className="imprint-card">
              <h3>AlpenScreen</h3>
              <p className="imprint-owner">{t('imprint.provider.owner')}</p>
              <div className="imprint-details">
                <div className="imprint-item">
                  <MapPin size={20} color="#2B7FD7" />
                  <div>
                    <strong>{t('imprint.provider.address.label')}</strong><br />
                    {t('imprint.provider.address.street')}<br />
                    {t('imprint.provider.address.city')}<br />
                    {t('imprint.provider.address.country')}
                  </div>
                </div>
                <div className="imprint-item">
                  <Mail size={20} color="#2B7FD7" />
                  <div>
                    <strong>{t('imprint.provider.email.label')}</strong><br />
                    info@alpenscreen.ch
                  </div>
                </div>
                <div className="imprint-item">
                  <Phone size={20} color="#2B7FD7" />
                  <div>
                    <strong>{t('imprint.provider.phone.label')}</strong><br />
                    +41 76 718 41 00
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="legal-section">
            <h2>{t('imprint.contact.title')}</h2>
            <div className="contact-grid">
              <div className="contact-box">
                <h3>{t('imprint.contact.general.title')}</h3>
                <p>
                  <Mail size={18} color="#2B7FD7" style={{ display: 'inline', marginRight: '8px' }} />
                  info@alpenscreen.ch
                </p>
              </div>
              <div className="contact-box">
                <h3>{t('imprint.contact.support.title')}</h3>
                <p>
                  <Mail size={18} color="#2B7FD7" style={{ display: 'inline', marginRight: '8px' }} />
                  support@alpenscreen.ch
                </p>
              </div>
              <div className="contact-box">
                <h3>{t('imprint.contact.privacy.title')}</h3>
                <p>
                  <Mail size={18} color="#2B7FD7" style={{ display: 'inline', marginRight: '8px' }} />
                  privacy@alpenscreen.ch
                </p>
              </div>
              <div className="contact-box">
                <h3>{t('imprint.contact.legal.title')}</h3>
                <p>
                  <Mail size={18} color="#2B7FD7" style={{ display: 'inline', marginRight: '8px' }} />
                  legal@alpenscreen.ch
                </p>
              </div>
            </div>
          </section>

          {/* Disclaimer Section */}
          <section className="legal-section">
            <h2>{t('imprint.disclaimer.title')}</h2>
            
            <h3>{t('imprint.disclaimer.content.title')}</h3>
            <p>{t('imprint.disclaimer.content.paragraph1')}</p>
            <p>{t('imprint.disclaimer.content.paragraph2')}</p>

            <h3>{t('imprint.disclaimer.links.title')}</h3>
            <p>{t('imprint.disclaimer.links.paragraph1')}</p>
            <p>{t('imprint.disclaimer.links.paragraph2')}</p>

            <h3>{t('imprint.disclaimer.copyright.title')}</h3>
            <p>{t('imprint.disclaimer.copyright.paragraph1')}</p>
            <p>{t('imprint.disclaimer.copyright.paragraph2')}</p>
          </section>

          {/* Dispute Resolution */}
          <section className="legal-section">
            <h2>{t('imprint.dispute.title')}</h2>
            <p>
              {t('imprint.dispute.paragraph1')}{' '}
              <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="inline-link">
                ec.europa.eu/consumers/odr/
              </a>.
            </p>
            <p>{t('imprint.dispute.paragraph2')}</p>
          </section>

          {/* Hosting & Infrastructure */}
          <section className="legal-section">
            <h2>{t('imprint.hosting.title')}</h2>
            <div className="info-box">
              <p>
                <strong>{t('imprint.hosting.server.title')}</strong><br />
                Hetzner Online GmbH<br />
                Industriestr. 25<br />
                91710 Gunzenhausen<br />
                {t('imprint.hosting.server.country')}
              </p>
              <p style={{ marginTop: '16px' }}>
                <strong>{t('imprint.hosting.payment.title')}</strong><br />
                Stripe, Inc.<br />
                510 Townsend Street<br />
                San Francisco, CA 94103<br />
                {t('imprint.hosting.payment.country')}
              </p>
            </div>
          </section>

          {/* Design & Development */}
          <section className="legal-section">
            <h2>{t('imprint.development.title')}</h2>
            <p>
              <strong>{t('imprint.development.website')}</strong> Stefano Rizzo<br />
              <strong>{t('imprint.development.branding')}</strong> AlpenScreen<br />
              <strong>{t('imprint.development.icons')}</strong> Lucide Icons (MIT License)
            </p>
          </section>

          {/* Image Credits */}
          <section className="legal-section">
            <h2>{t('imprint.credits.title')}</h2>
            <p>{t('imprint.credits.description')}</p>
            <ul>
              <li>{t('imprint.credits.logo')}</li>
              <li>{t('imprint.credits.graphics')}</li>
              <li>{t('imprint.credits.icons')}</li>
            </ul>
          </section>

          {/* Changes Section */}
          <section className="legal-section">
            <h2>{t('imprint.changes.title')}</h2>
            <p>{t('imprint.changes.paragraph1')}</p>
            <p className="legal-date">
              <strong>{t('imprint.changes.lastUpdate')}</strong> 27. Oktober 2025
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>{t('imprint.footer.copyright')}</p>
          <div className="legal-footer-links">
            <button onClick={() => navigate('/support')} className="footer-link">
              {t('imprint.footer.support')}
            </button>
            <span>•</span>
            <button onClick={() => navigate('/privacy')} className="footer-link">
              {t('imprint.footer.privacy')}
            </button>
            <span>•</span>
            <button onClick={() => navigate('/terms')} className="footer-link">
              {t('imprint.footer.terms')}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Imprint;